import React, { useEffect, useState, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Popup } from "semantic-ui-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Avatar,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

import StudentTableProps from "./Table_Props/StudentTableProps";
import axios from "../../../../axios";
import ConfirmSubmitStudent from "./ConfirmSubmitStudent";
import SuccessSubmitOnReview from "./SuccessSubmitOnReview";
import AssessmentCompletedList from "../../On_going/Published/Published_Q_list/AssessmentCompletedList";
import { timeHelper, dateHelper } from "../../../../Utils/datetime_helper";

const ReviewStudentList = () => {
  const { state } = useLocation();
  const [clickedSubmit, setClickedSubmit] = useState(false);
  const [studentList, setStudentList] = useState([]);
  const [completedStudentList, setCompletedStudentList] = useState([]);
  const [assessment, setAssessment] = useState();
  const [inGettingStudentProcess, setInGettingStudentProcess] = useState(false);
  const [allCompleted, setAllCompleted] = useState(false);
  const [click, setClick] = useState();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const navigate = useNavigate();

  const [filter, setFilter] = useState({ name: "" });
  const { name } = filter;

  const searchTimeout = useRef(null);
  const [debouncedName, setDebouncedName] = useState("");

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setDebouncedName(name), 300);
    return () => clearTimeout(searchTimeout.current);
  }, [name]);

  useEffect(() => {
    if (!assessment) return;
    const storedDate = assessment?.schedule_date;
    const storedTimeUTC = assessment?.schedule_time?.padStart(2, "0");
    const checkTime = () => {
      const currentDate = new Date();
      const targetDate = new Date(storedDate);
      const [h, m] = storedTimeUTC?.split(":").map(Number);
      targetDate?.setUTCHours(h, m, 0, 0);
      setIsButtonDisabled(currentDate < targetDate);
    };
    checkTime();
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, [assessment]);

  useEffect(() => {
    const getStudentList = async () => {
      setInGettingStudentProcess(true);
      try {
        const studentRes = await axios.get(
          `/api/v1/assessment/studentAssessment/getByAssessmentId/${
            state.assessmentId
          }?${debouncedName ? `name=${debouncedName}` : ""}`,
        );
        const assessmentRes = await axios.get(
          `/api/v1/assessment/get/${state.assessmentId}`,
        );
        setAssessment(assessmentRes.data.data);
        setStudentList(studentRes.data.data);
        const completed = studentRes.data.data.filter(
          (s) => s.status === "completed",
        );
        const allCompletedCheck = studentRes.data.data.every(
          (s) => s.status === "completed",
        );
        setCompletedStudentList(completed);
        setAllCompleted(allCompletedCheck);
      } catch (err) {
        console.log(err);
      } finally {
        setInGettingStudentProcess(false);
      }
    };
    getStudentList();
  }, [debouncedName]);

  const updateStatus = async () => {
    try {
      await axios.put(`/api/v1/assessment/updateStatus/${state.assessmentId}`, {
        status: "completed",
      });
    } catch (err) {
      console.log(err);
    }
  };

  const isReviewStatusPresent = studentList.some((s) => s.status === "review");
  const totalCompleted = completedStudentList.length;
  const totalStudents = studentList.length;
  const isAutoEval = !!assessment?.autoEvalEnabled;

  return (
    <Box
      className="container-fluid px-4 py-3"
      sx={{ bgcolor: "#f7f8fa", minHeight: "100vh" }}
    >
      {/* ───────── Compact top bar ───────── */}
      <Paper
        elevation={0}
        sx={{
          px: 2.5,
          py: 1.5,
          mb: 2,
          borderRadius: 2.5,
          border: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Left — back + title + small meta */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <Box
            onClick={() => navigate("/publishedAssessment")}
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f3f4f6",
              cursor: "pointer",
              flexShrink: 0,
              transition: "all .15s",
              "&:hover": { bgcolor: "#e5e7eb" },
            }}
          >
            <ArrowBackIosNewRoundedIcon
              sx={{ fontSize: 15, color: "#374151" }}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography fontWeight={700} fontSize={15} color="#111827" noWrap>
                {assessment?.name || "Loading…"}
              </Typography>

              <Chip
                label="ON REVIEW"
                size="small"
                sx={{
                  height: 20,
                  bgcolor: "#fef3c7",
                  color: "#b45309",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: 0.5,
                }}
              />

              {/* ⭐ Auto Evaluated badge */}
              {isAutoEval && (
                <Tooltip
                  arrow
                  title="Marks were generated automatically by the system"
                >
                  <Chip
                    icon={
                      <AutoAwesomeRoundedIcon
                        sx={{ fontSize: "14px !important" }}
                      />
                    }
                    label="AUTO EVALUATED"
                    size="small"
                    sx={{
                      height: 20,
                      bgcolor: "#e0e7ff",
                      color: "#3730a3",
                      fontWeight: 700,
                      fontSize: 10,
                      letterSpacing: 0.5,
                      "& .MuiChip-icon": { color: "#3730a3" },
                    }}
                  />
                </Tooltip>
              )}
            </Stack>

            <Typography
              fontSize={11}
              color="text.secondary"
              noWrap
              sx={{ mt: 0.2 }}
            >
              {assessment?.subject_id?.name || "—"} ·{" "}
              {dateHelper(assessment?.schedule_date || "")} ·{" "}
              {timeHelper(assessment?.schedule_time || "")}
            </Typography>
          </Box>
        </Stack>

        {/* Right — completed badge + publish */}
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Popup
            on="click"
            className="completed_list"
            style={{ zIndex: 2000 }}
            mountNode={document.body}
            trigger={
              <Paper
                elevation={0}
                onClick={() => setClick(true)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 0.5,
                  pr: 1.5,
                  py: 0.5,
                  border: "1px solid #e5e7eb",
                  borderRadius: 999,
                  cursor: "pointer",
                  transition: "all .15s",
                  "&:hover": {
                    borderColor: "#c7d2fe",
                    boxShadow: "0 4px 12px rgba(59,130,246,.12)",
                  },
                }}
              >
                <Avatar
                  src="/images/memberListIcon.png"
                  sx={{ width: 26, height: 26, border: "2px solid #eef2ff" }}
                />
                <Typography fontSize={12} fontWeight={700} color="#111827">
                  {totalCompleted}
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                  done
                </Typography>
              </Paper>
            }
            content={
              <AssessmentCompletedList
                completeList={completedStudentList}
                setClick={setClick}
              />
            }
            position="bottom right"
            hideOnScroll
          />

          <Tooltip
            arrow
            title={
              isButtonDisabled
                ? "Available after scheduled time"
                : isReviewStatusPresent
                  ? "Some students are still reviewing"
                  : "Publish results"
            }
          >
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
                disabled={isReviewStatusPresent || isButtonDisabled}
                onClick={() => {
                  setClickedSubmit(true);
                  if (allCompleted) updateStatus();
                }}
                sx={{
                  background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  boxShadow: "0 3px 10px rgba(37,99,235,.25)",
                  "&:hover": {
                    background: "linear-gradient(135deg,#1d4ed8,#1e40af)",
                    boxShadow: "0 5px 14px rgba(37,99,235,.35)",
                  },
                  "&.Mui-disabled": {
                    background: "#e5e7eb",
                    color: "#9ca3af",
                    boxShadow: "none",
                  },
                }}
              >
                Publish Result
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {/* ───────── Table card ───────── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Card header — search row */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={700} fontSize={14} color="#111827">
              Students
            </Typography>
            <Chip
              label={totalStudents}
              size="small"
              sx={{
                height: 20,
                minWidth: 24,
                bgcolor: "#eef2ff",
                color: "#1d4ed8",
                fontWeight: 700,
                fontSize: 11,
              }}
            />

            {isAutoEval && (
              <Chip
                icon={
                  <AutoAwesomeRoundedIcon
                    sx={{ fontSize: "13px !important" }}
                  />
                }
                label="Auto-scored"
                size="small"
                sx={{
                  height: 20,
                  bgcolor: "#ecfdf5",
                  color: "#047857",
                  fontWeight: 600,
                  fontSize: 10,
                  "& .MuiChip-icon": { color: "#047857" },
                }}
              />
            )}
          </Stack>

          <TextField
            size="small"
            placeholder="Search by student name…"
            value={name}
            onChange={(e) => setFilter({ ...filter, name: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    fontSize="small"
                    sx={{ color: "#9ca3af", fontSize: 18 }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "#f9fafb",
                fontSize: 13,
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#c7d2fe" },
                "&.Mui-focused fieldset": { borderColor: "#2563eb" },
              },
            }}
          />
        </Box>

        <Divider sx={{ borderColor: "#f3f4f6" }} />

        {/* Table */}
        <Box sx={{ maxHeight: 450, overflowY: "auto" }}>
          <Table
            borderless
            hover
            className="mb-0 align-middle"
            style={{
              textAlign: "center",
              borderCollapse: "separate",
              borderSpacing: 0,
              marginBottom: 0,
            }}
          >
            <thead>
              <tr>
                {[
                  "Name",
                  "Roll No.",
                  "Class",
                  "Division",
                  "Date submitted",
                  // ⭐ Marks header auto-eval ke hisaab se
                  isAutoEval ? "Auto Marks" : "Marks",
                  "Status",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                      backgroundColor: "#f9fafb",
                      color: "#6b7280",
                      fontWeight: 600,
                      fontSize: 12,
                      letterSpacing: 0.3,
                      textTransform: "uppercase",
                      padding: "14px 12px",
                      borderBottom: "1px solid #e5e7eb",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h === "Auto Marks" ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        spacing={0.5}
                      >
                        <AutoAwesomeRoundedIcon
                          sx={{ fontSize: 13, color: "#047857" }}
                        />
                        <span>{h}</span>
                      </Stack>
                    ) : (
                      h
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody style={{ fontSize: 13, color: "#374151" }}>
              {inGettingStudentProcess ? (
                <tr>
                  <td colSpan="7" style={{ padding: "56px 0" }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <CircularProgress size={26} sx={{ color: "#2563eb" }} />
                      <Typography fontSize={12} color="text.secondary">
                        Loading students…
                      </Typography>
                    </Stack>
                  </td>
                </tr>
              ) : studentList?.length > 0 ? (
                <StudentTableProps
                  studentList={studentList}
                  isAutoEval={isAutoEval}
                />
              ) : (
                <tr>
                  <td colSpan="7" style={{ padding: "56px 0" }}>
                    <Stack alignItems="center" spacing={0.75}>
                      <SearchOffRoundedIcon
                        sx={{ fontSize: 38, color: "#d1d5db" }}
                      />
                      <Typography
                        fontWeight={600}
                        fontSize={14}
                        color="#374151"
                      >
                        No student found
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        Try searching with a different name.
                      </Typography>
                    </Stack>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Box>
      </Paper>

      {/* ───────── Popups ───────── */}
      {clickedSubmit &&
        (allCompleted ? (
          <SuccessSubmitOnReview assessmentId={assessment?.id} />
        ) : (
          <ConfirmSubmitStudent
            setClickedSubmit={setClickedSubmit}
            assessmentId={assessment?.id}
          />
        ))}
    </Box>
  );
};

export default ReviewStudentList;
